import { useEffect, useCallback, useState } from 'react';
import { VIEWPORT_CONFIG } from '@/utils/constants';
import { getGameInstance } from '@/game';

/**
 * ViewportScaler - 画面解像度に応じてレイアウト全体を縮小するコンポーネント
 *
 * 基準解像度（1920x1080）より小さい画面では、transform: scale()で全体を縮小し、
 * レイアウトを維持する。
 */
export const ViewportScaler: React.FC = () => {
  const [scaleStyle, setScaleStyle] = useState<React.CSSProperties>({});

  const updateScale = useCallback(() => {
    const { BASE_WIDTH, BASE_HEIGHT, MIN_SCALE } = VIEWPORT_CONFIG;

    // 現在のウィンドウサイズ
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // 縮小率を計算（幅と高さの両方を考慮し、小さい方を採用）
    const scaleX = windowWidth / BASE_WIDTH;
    const scaleY = windowHeight / BASE_HEIGHT;

    // 1.0を超えない（拡大はしない）、MIN_SCALE未満にならない
    const scale = Math.max(Math.min(scaleX, scaleY, 1.0), MIN_SCALE);

    // スタイルを更新（scale < 1の場合のみスケーリング適用）
    if (scale < 1) {
      setScaleStyle({
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        width: `${BASE_WIDTH}px`,
        height: `${BASE_HEIGHT}px`,
      });
    } else {
      setScaleStyle({
        width: '100vw',
        height: '100vh',
      });
    }

  }, []);

  useEffect(() => {
    // 初期スケール設定
    updateScale();

    // リサイズイベントを監視
    window.addEventListener('resize', updateScale);

    // クリーンアップ
    return () => {
      window.removeEventListener('resize', updateScale);
    };
  }, [updateScale]);

  // スケーリングコンテナのスタイルをCSS変数として設定
  useEffect(() => {
    const root = document.documentElement;
    if (scaleStyle.transform) {
      root.style.setProperty('--viewport-scale-transform', scaleStyle.transform);
      root.style.setProperty('--viewport-scale-width', scaleStyle.width as string);
      root.style.setProperty('--viewport-scale-height', scaleStyle.height as string);
      root.style.setProperty('--viewport-scaled', '1');
    } else {
      root.style.setProperty('--viewport-scaled', '0');
      root.style.removeProperty('--viewport-scale-transform');
      root.style.removeProperty('--viewport-scale-width');
      root.style.removeProperty('--viewport-scale-height');
    }

    // CSS変換反映後にPhaser入力境界を同期
    let rafId: number | null = null;
    const game = getGameInstance();
    if (game?.scale) {
      rafId = requestAnimationFrame(() => {
        if (!game.scale) return;
        game.scale.updateBounds();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inputManager = game.input as any;
        if (inputManager?.updateBounds) {
          inputManager.updateBounds();
        }
      });
    }

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [scaleStyle]);

  // このコンポーネントはUIを持たない
  return null;
};
